import { ReactElement } from 'react';
import { Endorsement, LifeCoverageCodes, LifePossession } from '../../../../../../shared/interfaces';
import { DocumentType } from '../../../../../../shared/interfaces/enums/document-type-enum';
import {
  formatDate,
  formatNumberAR,
  formattedPrice,
  getInsuredIsValid,
} from '../../../../../../shared/utils';
import { Column } from '../../../utils/renderSections';

export const INSURED_SELECTION_DOCUMENT_TYPES: DocumentType[] = [
  DocumentType.ConstanciaCoberturaAPConClausula,
  DocumentType.ConstanciaCoberturaAPSinClausula,
  DocumentType.ConstanciaCoberturaAPBeneficiarioPrimerTermino,
  DocumentType.ConstanciaCoberturaVidaObligatorioCCVOB,
  DocumentType.ConstanciaCoberturaVidaObligatorioVIDOB,
  DocumentType.ConstanciaCoberturaSepelio,
  DocumentType.ConstanciaCoberturaVidaColectivo,
  DocumentType.ConstanciaCoberturaVidaSaldoDeudor,
];

export interface InsuredDocumentPayload {
  integrante: string;
  documentoTipo: string;
  documentoNumero: string;
  numeroCuil: string;
  actividad: string;
  descripcionActividad: string;
  fechaEgreso: string;
  estaVigente: string;
  coberturas: Array<{
    coberturaNombre: string;
    moneda?: string;
    sumaAsegurada: string;
  }>;
}

export const getInsuredSelectionKey = (
  person: Pick<LifePossession, 'nroDocVida' | 'integrVida'>
): string => `${person.nroDocVida}-${person.integrVida}`;

export const requiresInsuredSelection = (documentType: string): boolean =>
  INSURED_SELECTION_DOCUMENT_TYPES.includes(documentType as DocumentType);

export const mapLifePossessionToInsured = (
  item: LifePossession,
  endorsement?: Endorsement
): InsuredDocumentPayload => {
  const isFirstComponent = String(item.nroComponente) === '1';

  return {
    integrante: item.integrVida,
    documentoTipo: item.tipDocVida,
    documentoNumero: item.nroDocVida,
    numeroCuil: item.numeroCuil,
    actividad: item.actividad,
    descripcionActividad: item.descripcionActividad,
    fechaEgreso: item.fecEgreso,
    estaVigente: item.estaVigente,
    coberturas: item.coberturas
      ?.filter(
        (coverage) =>
          isFirstComponent ||
          String(coverage.codCobert) !== LifeCoverageCodes.ADDITIONAL_COVERAGE
      )
      ?.map((coverage) => ({
        coberturaNombre: coverage.nombreCob,
        moneda: endorsement?.moneda,
        sumaAsegurada: formatNumberAR(coverage.sumaCob),
      })) ?? [],
  };
};

export const buildInsuredCardColumns = (
  person: LifePossession,
  selectionControl: ReactElement
): Column[] => [
  {
    label: 'Seleccionar',
    value: selectionControl,
    columnSize: {
      xs: { span: 24 },
      sm: { span: 12 },
      md: { span: 8 },
      lg: { span: 2 },
    },
  },
  {
    label: 'Nombre y Apellido',
    value: person.integrVida,
    columnSize: {
      xs: { span: 12 },
      sm: { span: 12 },
      md: { span: 8 },
      lg: { span: 6 },
    },
  },
  {
    label: person.tipDocVida,
    value: person.nroDocVida,
    columnSize: {
      xs: { span: 12 },
      sm: { span: 12 },
      md: { span: 8 },
      lg: { span: 4 },
    },
  },
  {
    label: 'Fecha de Nacimiento',
    value: formatDate(person.fecNacVida),
    columnSize: {
      xs: { span: 12 },
      sm: { span: 12 },
      md: { span: 8 },
      lg: { span: 4 },
    },
  },
  {
    label: 'Suma Asegurada',
    value: formattedPrice(person.sumAseVida),
    columnSize: {
      xs: { span: 12 },
      sm: { span: 12 },
      md: { span: 8 },
      lg: { span: 5 },
    },
  },
  {
    label: 'Vigencia',
    value: getInsuredIsValid(person.estaVigente) ? 'Vigente' : 'No vigente',
    columnSize: {
      xs: { span: 12 },
      sm: { span: 12 },
      md: { span: 8 },
      lg: { span: 3 },
    },
  },
];

export const getInsuredSelectionSummary = (
  total: number,
  selectedKeys: string[],
  allPersons: LifePossession[]
) => {
  const selectedCount = allPersons.filter((person) =>
    selectedKeys.includes(getInsuredSelectionKey(person))
  ).length;

  return {
    selectedCount,
    allSelected: total > 0 && selectedCount === total,
    someSelected: selectedCount > 0 && selectedCount < total,
  };
};
